'use client';

import type { Address } from '@baseball-near-you/prisma';

import { type ComponentType, Suspense, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { Button, Dialog, DialogContent, DialogTrigger, Skeleton } from '../ui';
import { Form } from './Form';
import { FormInput } from './FormInput';

import type { AddressAutofillProps } from '@mapbox/search-js-react/dist/components/AddressAutofill';
import { lazy } from 'react';
const AddressAutofill = lazy(() =>
  import('@mapbox/search-js-react').then((mod) => ({
    default: mod.AddressAutofill as ComponentType<AddressAutofillProps>,
  }))
);

type AddressFormValues = Pick<
  Address,
  | 'street'
  | 'street2'
  | 'city'
  | 'state'
  | 'postalCode'
  | 'country'
  | 'lat'
  | 'lng'
>;

export interface AddressFormProps {
  address?: Address;
  /**
   * if there is a prefilled address, this will be called when
   * its cleared out inside the form (new auto suggest from mapbox)
   *  */
  onNewAddress?: () => void;
  onSubmit?: (data: AddressFormValues) => Promise<void>;
  isSubmitting: boolean;
  buttonLabel: string;
  onSkip?: () => void;
  accessToken: string;
}

export function AddressForm(props: AddressFormProps) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return isClient ? (
    <Form<AddressFormValues & { suggest: string | null }>
      onSubmit={async ({ suggest, ...data }) => {
        await props.onSubmit?.(data);
      }}
      schema={z.object({
        street: z.string(),
        street2: z.string(),
        city: z.string(),
        state: z.string(),
        postalCode: z.string(),
        country: z.string(),
        lng: z.number().nullable(),
        lat: z.number().nullable(),
        suggest: z.string().nullable(),
      })}
      initialValues={{
        street: props.address?.street ?? '',
        street2: props.address?.street2 ?? '',
        city: props.address?.city ?? '',
        state: props.address?.state ?? '',
        postalCode: props.address?.postalCode ?? '',
        country: props.address?.country ?? '',
        lng: props.address?.lng ?? null,
        lat: props.address?.lat ?? null,
        suggest: '',
      }}
      className="w-full space-y-5"
    >
      <InnerAddressForm
        isSubmitting={props.isSubmitting}
        buttonLabel={props.buttonLabel}
        onSkip={props.onSkip}
        accessToken={props.accessToken}
        address={props.address}
        onNewAddress={props.onNewAddress}
      />
    </Form>
  ) : null;
}

type Suggestion = {
  address_line1: string;
  address_line2: string;
  address_level2: string;
  address_level1: string;
  country: string;
  postcode: string;
};

// const formatMapboxSuggestion = (
//   suggestion: Suggestion
// ): Omit<
//   Address,
//   "isPrimary" | "id" | "createdAt" | "updatedAt" | "lat" | "lng" | "citySlug"
// > => {
//   const {
//     address_line1: streetOne,
//     address_line2: streetTwo,
//     address_level2: city,
//     address_level1: stateCode,
//     country,
//     postcode: postalCode,
//   } = suggestion;

//   return {
//     street: streetOne,
//     street2: streetTwo,
//     city,
//     state: stateCode,
//     country,
//     postalCode,
//   };
// };

export function InnerAddressForm(props: {
  isSubmitting: boolean;
  buttonLabel: string;
  onSkip?: () => void;
  accessToken: string;
  address?: Address;
  onNewAddress?: () => void;
}) {
  const {
    setValue,
    formState: { isDirty },
    reset,
  } = useFormContext();

  const [addressRetrieved, setAddressRetrieved] = useState(false);

  useEffect(() => {
    if (props.address) {
      setAddressRetrieved(false);

      setValue('street', props.address.street);
      setValue('street2', props.address.street2);
      setValue('city', props.address.city);
      setValue('state', props.address.state);
      setValue('postalCode', props.address.postalCode);
      setValue('country', props.address.country);
      setValue('lng', props.address.lng);
      setValue('lat', props.address.lat);
    } else {
      // cleared by user
      if (!addressRetrieved) {
        reset();
      }
    }
  }, [props.address, setValue, addressRetrieved]);

  return (
    <>
      {/* @ts-ignore */}
      <Suspense fallback={<Skeleton />}>
        <AddressAutofill
          accessToken={props.accessToken}
          theme={{
            cssText: `
            z-index: 9999;
          `,
          }}
          onRetrieve={(data: any) => {
            setAddressRetrieved(true);
            console.log('onRetrieve', data);
            const {
              address_line1: streetOne,
              address_line2: streetTwo,
              address_level2: city,
              address_level1: stateCode,
              country,
              postcode: postalCode,
            } = data.features[0].properties;
            const [long, lat] = data.features[0].geometry.coordinates;

            if (props.onNewAddress) {
              props.onNewAddress();
            }

            setValue('street', streetOne);
            setValue('street2', streetTwo);
            setValue('city', city);
            setValue('state', stateCode);
            setValue('country', country);
            setValue('postalCode', postalCode);

            setValue('lng', long);
            setValue('lat', lat);
          }}
        >
          <FormInput
            name="street"
            label="Street"
            placeholder="Enter your address..."
            className="mb-5"
          />
        </AddressAutofill>
      </Suspense>
      <FormInput name="street2" placeholder="Street 2" label="Street 2" />
      <FormInput
        name="city"
        placeholder="City"
        label="City"
        className="pointer-events-none bg-gray-100"
      />
      <FormInput
        name="state"
        placeholder="State"
        label="State"
        className="pointer-events-none bg-gray-100"
      />
      <FormInput
        name="postalCode"
        placeholder="Zip code"
        label="Zip code"
        className="pointer-events-none bg-gray-100"
      />
      <FormInput
        name="country"
        placeholder="Country"
        label="Country"
        onChange={(e) => {
          console.log(e.target.value);
        }}
        disabled
        className="pointer-events-none bg-gray-100"
      />
      <div className="mt-6! flex w-full justify-between space-x-5">
        {!!props.onSkip && (
          <Button
            className="w-1/2"
            variant={'outline'}
            onClick={() => {
              props.onSkip?.();
            }}
            type="button"
          >
            Skip
          </Button>
        )}
        <Button
          className="w-full"
          variant={'default'}
          disabled={!addressRetrieved && !isDirty && !props.address}
          loading={props.isSubmitting}
        >
          {props.buttonLabel}
        </Button>
      </div>
    </>
  );
}

// TODO Why does the suggest dropdown not work in this modal?
export function AddressFormModal({
  triggerButtonLabel,
  ...props
}: AddressFormProps & {
  triggerButtonLabel: string;
}) {
  return (
    <Dialog>
      <DialogTrigger>
        <Button>{triggerButtonLabel ?? 'Open modal'}</Button>
      </DialogTrigger>
      <DialogContent>
        <AddressForm {...props} />
      </DialogContent>
    </Dialog>
  );
}
